import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/api";
import {
  patchWorkflow,
  startOrResumeWorkflow,
} from "@/features/workflows/api";
import type {
  ResolutionWorkflow,
  SelectedForm,
  WorkflowStep,
} from "@/features/workflows/types";

interface State {
  workflow: ResolutionWorkflow | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loads (creating on first access, idempotently) the IA workflow for a case and
 * exposes progress mutators. The wizard routes on the `:step` URL param, so this
 * hook only persists progress + selected form; it never owns the active step.
 */
export function useIaWorkflow(caseId: string) {
  const [state, setState] = useState<State>({
    workflow: null,
    loading: true,
    error: null,
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    startOrResumeWorkflow(caseId)
      .then((workflow) => {
        if (mounted.current) setState({ workflow, loading: false, error: null });
      })
      .catch((err) => {
        if (!mounted.current) return;
        setState({
          workflow: null,
          loading: false,
          error: err instanceof ApiError ? err.message : "Couldn't start the workflow.",
        });
      });
    return () => {
      mounted.current = false;
    };
  }, [caseId]);

  const setWorkflow = useCallback((workflow: ResolutionWorkflow) => {
    if (mounted.current) setState((s) => ({ ...s, workflow }));
  }, []);

  /** Persist the current step as the resume target (fire-and-forget UI-wise). */
  const persistStep = useCallback(
    async (step: WorkflowStep) => {
      const wf = state.workflow;
      if (!wf || wf.currentStep === step) return;
      try {
        const updated = await patchWorkflow(caseId, wf.id, { currentStep: step });
        setWorkflow(updated);
      } catch {
        // Progress is a resume convenience — a failed persist shouldn't block
        // navigation; the next successful patch corrects it.
      }
    },
    [caseId, state.workflow, setWorkflow],
  );

  const selectForm = useCallback(
    async (selectedForm: SelectedForm) => {
      const wf = state.workflow;
      if (!wf) return;
      const updated = await patchWorkflow(caseId, wf.id, { selectedForm });
      setWorkflow(updated);
    },
    [caseId, state.workflow, setWorkflow],
  );

  return {
    workflow: state.workflow,
    loading: state.loading,
    error: state.error,
    setWorkflow,
    persistStep,
    selectForm,
  };
}
